import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Lease } from '../../types/leases';
import { formatCurrency } from '../../Helpers/FormatUtils';
import { leaseObjectLabel, leaseStatus, leaseStatusLabel, leasesPath } from '../../Helpers/leaseUtils';
import { rentPer } from '../Leases/leasesView';

interface TenantLeaseRowsProps {
    current: Lease[];
    ended: Lease[];
    buildingName: (buildingKey: string) => string;
    /** The object as shown (with the unit's stairwell); defaults to leaseObjectLabel. */
    objectLabel?: (lease: Lease) => string;
}

const LeaseRow: React.FC<{ lease: Lease; buildingName: (buildingKey: string) => string; objectLabel: (lease: Lease) => string }> = ({ lease, buildingName, objectLabel }) => {
    const status = leaseStatus(lease);
    return (
        <li>
            <Link to={leasesPath(lease.buildingKey, lease.key)} className={`tenant-lease-row ${status === 'ended' ? 'tenant-lease-row--ended' : ''}`}>
                <span className="tenant-lease-row-object">
                    <strong>{objectLabel(lease)}</strong>
                    <span className="tenants-muted">{buildingName(lease.buildingKey)}</span>
                </span>
                <span className="tenant-lease-row-period">{lease.startDate} – {lease.endDate ?? 'tills vidare'}</span>
                <span className="tenant-lease-row-amount">{formatCurrency(rentPer(lease, 'month'))}/mån</span>
                <span className={`lease-status lease-status--${status}`}>{leaseStatusLabel(status)}</span>
            </Link>
        </li>
    );
};

/**
 * The tenant's leases in the pane, one compact row each that opens the lease
 * on Hyresavtal; the ended ones fold under "Tidigare (n)".
 */
const TenantLeaseRows: React.FC<TenantLeaseRowsProps> = ({ current, ended, buildingName, objectLabel = leaseObjectLabel }) => {
    const [showEnded, setShowEnded] = useState(false);
    return (
        <>
            {current.length === 0 ? (
                <p className="text-muted">Inga pågående hyresavtal. Avtal skapas under Hyresavtal.</p>
            ) : (
                <ul className="tenant-lease-rows">
                    {current.map(lease => <LeaseRow key={lease.key} lease={lease} buildingName={buildingName} objectLabel={objectLabel} />)}
                </ul>
            )}
            {ended.length > 0 && (
                <>
                    <button type="button" className="tenant-lease-ended-toggle" aria-expanded={showEnded} onClick={() => setShowEnded(v => !v)}>
                        {showEnded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        Tidigare ({ended.length})
                    </button>
                    {showEnded && (
                        <ul className="tenant-lease-rows">
                            {ended.map(lease => <LeaseRow key={lease.key} lease={lease} buildingName={buildingName} objectLabel={objectLabel} />)}
                        </ul>
                    )}
                </>
            )}
        </>
    );
};

export default TenantLeaseRows;
